import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Grid,
  Typography,
  TextField,
  Button,
  MenuItem,
  Fab,
  Icon,
  FormControl,
  InputLabel,
  Select,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { saveReferralRule } from 'api';
import { Formik, FieldArray } from 'formik';
import { InfoTooltip } from 'components';
import * as yup from 'yup';


const defaultCustomerGroups = [
  {
    id: 0,
    code: 'Not Logged In'
  }
];

const defaultActionData = {
  attribute: 'sku',
  operator: '',
  value: ''
};

const EditReferralRule = ({ setOpenEditRuleDialog, products, categories, setOpenDialogRewardPage }) => {
  const dispatch = useDispatch();
  const { editableReferralRule } = useSelector((state) => state.referral_program);
  const { customerGroups = [], websiteId } = useSelector((state) => state.customers);
  const allCustomerGroups = [...defaultCustomerGroups, ...customerGroups];

  const handleChangeAggregator = (setValues, event) => {
    const { value: stringValue } = event.target;
    const [aggregator, aggregator_value] = stringValue.split(',');
    setValues((values) => ({...values, aggregator, aggregator_value }));
  };

  const handleSubmit = async (values) => {
    const isSuccess = await saveReferralRule({dispatch, referralRuleData: values});
    setOpenEditRuleDialog(false);

    if (isSuccess) {
      setOpenDialogRewardPage(true);
    }
  };

  return (
    <div className="pt-10 pl-6">
      <Formik
        initialValues={{
          rule_id: editableReferralRule.rule_id,
          name: editableReferralRule.name || '',
          description: editableReferralRule.description || '',
          sort_order: editableReferralRule.sort_order || 1,
          to_date: editableReferralRule.to_date || null,
          from_date: editableReferralRule.from_date || null,
          is_active: editableReferralRule.is_active ? '1' : '0',
          aggregator: editableReferralRule.aggregator || 'all',
          aggregator_value: editableReferralRule.aggregator_value || '1',
          website_ids: editableReferralRule.website_ids || websiteId,
          referral_group_ids: editableReferralRule.referral_group_ids || [],
          customer_group_ids: editableReferralRule.customer_group_ids || [],
          actions: editableReferralRule.actions || [{attribute: 'sku', operator: '', value: ''}],
          customer_action: editableReferralRule.customer_action || '',
          customer_discount: editableReferralRule.customer_discount || 0,
          customer_points: editableReferralRule.customer_points || 0,
          customer_money_step: editableReferralRule.customer_money_step || 0,
          customer_apply_to_shipping: editableReferralRule.customer_apply_to_shipping || false,
          stop_rules_processing: editableReferralRule.stop_rules_processing || false,
          referral_type: editableReferralRule.referral_type || '',
          referral_points: editableReferralRule.referral_points || 0,
          referral_money_step: editableReferralRule.referral_money_step || 0
        }}
        enableReinitialize
        validationSchema={
          yup.object({
            name: yup
              .string()
              .required('Field is required'),
            is_active: yup
              .string()
              .required('Field is required'),
            customer_group_ids: yup
              .array()
              .min(1, 'Choose a customer group')
              .required('Field is required'),
            referral_group_ids: yup
              .array()
              .min(1, 'Choose a referral group')
              .required('Field is required'),
            customer_action: yup
              .string(),
            customer_discount: yup
              .number()
              .min(0, 'Only positive numbers are allowed')
              .required('Field is required'),
            customer_points: yup
              .number()
              .min(0, 'Only positive numbers are allowed')
              .required('Field is required'),
            customer_money_step: yup
              .number()
              .min(0, 'Only positive numbers are allowed')
              .required('Field is required'),
            referral_points: yup
              .number()
              .min(0, 'Only positive numbers are allowed')
              .required('Field is required'),
            referral_money_step: yup
              .number()
              .min(0, 'Only positive numbers are allowed')
              .required('Field is required'),
            actions: yup
              .array().of(
                yup.object().shape({
                  attribute: yup
                    .string()
                    .required('Field is required'),
                  operator: yup
                    .string()
                    .required('Field is required'),
                  value: yup
                    .string()
                    .required('Field is required'),
                })
              )
          })
        }
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setValues,
          setFieldValue
        }) => {
          return (
            <form onSubmit={handleSubmit} noValidate>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h5">
                    Rule Information:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Rule Name <span className="text-error">*</span>
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    label="Name"
                    name="name"
                    className="max-w-600"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Description
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    multiline
                    fullWidth
                    rows={2}
                    variant="outlined"
                    label="Description"
                    name="description"
                    className="max-w-600"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{
                      className: 'p-0'
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Status
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    fullWidth
                    select
                    variant="outlined"
                    label="Rule Status"
                    name="is_active"
                    className="max-w-600"
                    value={values.is_active}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.is_active && Boolean(errors.is_active)}
                    helperText={touched.is_active && errors.is_active}
                  >
                    <MenuItem value="1">Active</MenuItem>
                    <MenuItem value="0">Inactive</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Customer Groups <span className="text-error">*</span>
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <Autocomplete
                    multiple
                    fullWidth
                    disableClearable
                    size="small"
                    className="max-w-600"
                    name="customer_group_ids"
                    options={allCustomerGroups}
                    value={allCustomerGroups.filter((g) => values.customer_group_ids.includes(String(g.id)))}
                    getOptionLabel={(option) => option.code}
                    onChange={(_, value, action, option) => {
                      const selectedOptionId = String(option?.option?.id);

                      if (action === 'select-option') {
                        setFieldValue(
                          'customer_group_ids',
                          value !== null ?
                            [...values.customer_group_ids, selectedOptionId]
                            : values.customer_group_ids
                        );
                      }

                      if (action === 'remove-option') {
                        setFieldValue(
                          'customer_group_ids',
                          value !== null ?
                            values.customer_group_ids.filter((id) => id !== selectedOptionId)
                            : values.customer_group_ids
                        );
                      }
                    }}
                    onBlur={(e, value, action, option) => {
                      const selectedOptionId = String(option?.option?.id);

                      if (action === 'select-option') {
                        setFieldValue(
                          'customer_group_ids',
                          value !== null ?
                            [...values.customer_group_ids, selectedOptionId]
                            : values.customer_group_ids
                        );
                      }

                      if (action === 'remove-option') {
                        setFieldValue(
                          'customer_group_ids',
                          value !== null ?
                            values.customer_group_ids.filter((id) => id !== selectedOptionId)
                            : values.customer_group_ids
                        );
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        variant="outlined"
                        label="Customer Groups"
                        placeholder="Choose customer group"
                        error={touched.customer_group_ids && Boolean(errors.customer_group_ids)}
                        helperText={touched.customer_group_ids && errors.customer_group_ids}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Referral Groups <span className="text-error">*</span>
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <Autocomplete
                    multiple
                    fullWidth
                    disableClearable
                    size="small"
                    className="max-w-600"
                    name="referral_group_ids"
                    value={allCustomerGroups.filter((g) => values.referral_group_ids.includes(String(g.id)))}
                    options={allCustomerGroups}
                    getOptionLabel={(option) => option.code}
                    onChange={(_, value, action, option) => {
                      const selectedOptionId = String(option?.option?.id);

                      if (action === 'select-option') {
                        setFieldValue(
                          'referral_group_ids',
                          value !== null ?
                            [...values.referral_group_ids, selectedOptionId]
                            : values.referral_group_ids
                        );
                      }

                      if (action === 'remove-option') {
                        setFieldValue(
                          'referral_group_ids',
                          value !== null ?
                            values.referral_group_ids.filter((id) => id !== selectedOptionId)
                            : values.referral_group_ids
                        );
                      }
                    }}
                    onBlur={(e, value, action, option) => {
                      const selectedOptionId = String(option?.option?.id);

                      if (action === 'select-option') {
                        setFieldValue(
                          'referral_group_ids',
                          value !== null ?
                            [...values.referral_group_ids, selectedOptionId]
                            : values.referral_group_ids
                        );
                      }

                      if (action === 'remove-option') {
                        setFieldValue(
                          'referral_group_ids',
                          value !== null ?
                            values.referral_group_ids.filter((id) => id !== selectedOptionId)
                            : values.referral_group_ids
                        );
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        variant="outlined"
                        label="Referral Groups"
                        placeholder="Choose referral group"
                        error={touched.referral_group_ids && Boolean(errors.referral_group_ids)}
                        helperText={touched.referral_group_ids && errors.referral_group_ids}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h5">
                    Customer Action:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Action
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    fullWidth
                    select
                    variant="outlined"
                    label="Action"
                    name="customer_action"
                    className="max-w-600"
                    value={values.customer_action}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.customer_action && Boolean(errors.customer_action)}
                    helperText={touched.customer_action && errors.customer_action}
                  >
                    <MenuItem value="fixed_points">Give fixed X points to Customers</MenuItem>
                    <MenuItem value="price">Give X points for every Y amount of Price</MenuItem>
                    <MenuItem value="fixed_discount">Give a fixed discount to Customer</MenuItem>
                    <MenuItem value="percent_discount">Give percent discount to Customer</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  {(values.customer_action === 'fixed_discount' ||
                  values.customer_action === 'percent_discount')
                    ? <>Discount <span className="text-error">*</span></>
                    : <>Points (X) <span className="text-error">*</span></>
                  }
                </Grid>
                {(values.customer_action === 'fixed_points' ||
                  values.customer_action === 'price') &&
                  <Grid item xs={12} sm={8} md={9}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="Points (X)"
                      name="customer_points"
                      className="max-w-600"
                      value={values.customer_points}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.customer_points && Boolean(errors.customer_points)}
                      helperText={touched.customer_points && errors.customer_points}
                    />
                  </Grid>
                }
                {(values.customer_action === 'fixed_discount' ||
                  values.customer_action === 'percent_discount') &&
                  <Grid item xs={12} sm={8} md={9}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="Discount"
                      name="customer_discount"
                      className="max-w-600"
                      value={values.customer_discount}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.customer_discount && Boolean(errors.customer_discount)}
                      helperText={touched.customer_discount && errors.customer_discount}
                    />
                  </Grid>
                }
                {values.customer_action === 'price' &&
                  <>
                    <Grid item xs={12} sm={4} md={3}>
                      Money Step (Y) <span className="text-error">*</span>
                    </Grid>
                    <Grid item xs={12} sm={8} md={9}>
                      <TextField
                        required
                        fullWidth
                        variant="outlined"
                        label="Money Step (Y)"
                        name="customer_money_step"
                        className="max-w-600"
                        value={values.customer_money_step}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.customer_money_step && Boolean(errors.customer_money_step)}
                        helperText={touched.customer_money_step && errors.customer_money_step}
                      />
                    </Grid>
                  </>
                }
                <Grid item xs={12}>
                  <Typography variant="h5">
                    Referral’s Action:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Referral’s Action type
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    fullWidth
                    select
                    variant="outlined"
                    label="Referral's action type"
                    name="referral_type"
                    className="max-w-600"
                    value={values.referral_type}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.referral_type && Boolean(errors.referral_type)}
                    helperText={touched.referral_type && errors.referral_type}
                  >
                    <MenuItem value="fixed">Give fixed X points to Referer</MenuItem>
                    <MenuItem value="price">Give X points for every Y amount of Price</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Referral’s points (X) <span className="text-error">*</span>
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    label="Referral’s points (X)"
                    name="referral_points"
                    className="max-w-600"
                    value={values.referral_points}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.referral_points && Boolean(errors.referral_points)}
                    helperText={touched.referral_points && errors.referral_points}
                  />
                </Grid>
                {values.referral_type === 'price' &&
                  <>
                    <Grid item xs={12} sm={4} md={3}>
                      Referral&apos;s Money Step (Y) <span className="text-error">*</span>
                    </Grid>
                    <Grid item xs={12} sm={8} md={9}>
                      <TextField
                        required
                        fullWidth
                        variant="outlined"
                        label="Referral's Money Step (Y)"
                        name="referral_money_step"
                        className="max-w-600"
                        value={values.referral_money_step}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.referral_money_step && Boolean(errors.referral_money_step)}
                        helperText={touched.referral_money_step && errors.referral_money_step}
                      />
                    </Grid>
                  </>
                }
                <Grid item xs={12}>
                  <Typography variant="h5">
                    Rule Actions:
                    <InfoTooltip title="You can skip these fields if you don't want to add additional conditions" />
                  </Typography>
                </Grid>
                <FieldArray name="actions">
                  {({remove, push}) => (
                    <>
                      {!values.actions.length ?
                        <Fab
                          color="primary"
                          size="small"
                          className="m-4"
                          onClick={() => push(defaultActionData)}
                          title="Add new rule action"
                        >
                          <Icon>add</Icon>
                        </Fab>
                        :
                        <>
                          <Grid  item xs={4} >
                            <FormControl size="small" fullWidth>
                              <InputLabel id="condition">Type</InputLabel>
                              <Select
                                labelId="type"
                                name="type"
                                defaultValue="all,1"
                                value={`${values.aggregator},${values.aggregator_value}`}
                                onChange={(e) => handleChangeAggregator(setValues, e)}
                              >
                                <MenuItem value="all,1">If ALL of next conditions are TRUE</MenuItem>
                                <MenuItem value="all,0">If ALL of next conditions are FALSE</MenuItem>
                                <MenuItem value="any,1">If ANY of next conditions are TRUE</MenuItem>
                                <MenuItem value="any,0">If ANY of next conditions are FALSE</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          {values.actions.map((_, idx) => {
                            const addRemoveSelectOption = (_, value, action, option) => {
                              if (action === 'select-option') {
                                setFieldValue(
                                  `actions.${idx}.value`,
                                  value.length != 0 ?
                                    [...values.actions[idx].value.split(', '), String(option.option.sku)].join(', ')
                                    : value.map((v) => v.id).join('')
                                );
                              }

                              if (action === 'remove-option') {
                                const filteredValues = values.actions[idx].value.split(', ').filter((sku) => sku != option.option.sku);
                                setFieldValue(
                                  `actions.${idx}.value`,
                                  filteredValues.join(', ')
                                );
                              }
                            };
                            return (
                              <Grid  item xs={12} key={idx}>
                                <Grid container spacing={3}>
                                  <Grid item xs={12} sm={4} md={3}>
                                    <TextField
                                      fullWidth
                                      select
                                      label="Select Type"
                                      name={`actions.${idx}.attribute`}
                                      value={values.actions[idx].attribute}
                                      onChange={(event) => {
                                        if (values.actions[idx].attribute !== event.target.value) {
                                          setFieldValue(`actions.${idx}.operator`, '');
                                          setFieldValue(`actions.${idx}.value`, '');
                                        }

                                        handleChange(event);
                                      }}
                                      onBlur={handleBlur}
                                      error={touched.actions?.length &&
                                          errors.actions?.length &&
                                          touched?.actions[idx]?.attribute &&
                                          Boolean(errors?.actions[idx]?.attribute)
                                      }
                                      helperText={touched.actions?.length &&
                                          errors.actions?.length &&
                                          touched?.actions[idx]?.attribute &&
                                          errors?.actions[idx]?.attribute
                                      }
                                    >
                                      <MenuItem value="sku">SKU</MenuItem>
                                      <MenuItem value="category_ids">Category</MenuItem>
                                    </TextField>
                                  </Grid>
                                  <Grid item xs={12} sm={4} md={3}>
                                    {values.actions[idx].attribute === 'category_ids' &&
                                      <TextField
                                        fullWidth
                                        select
                                        label="Operator"
                                        name={`actions.${idx}.operator`}
                                        value={values.actions[idx].operator}
                                        onChange={(event) => {
                                          if (values.actions[idx].operator !== event.target.value) {
                                            setFieldValue(`actions.${idx}.value`, '');
                                          }

                                          handleChange(event);
                                        }}
                                        onBlur={handleBlur}
                                        error={touched.actions?.length &&
                                            errors.actions?.length &&
                                            touched?.actions[idx]?.operator &&
                                            Boolean(errors?.actions[idx]?.operator)
                                        }
                                        helperText={touched.actions?.length &&
                                            errors.actions?.length &&
                                            touched?.actions[idx]?.operator &&
                                            errors?.actions[idx]?.operator
                                        }
                                      >
                                        <MenuItem value="==">is</MenuItem>
                                        <MenuItem value="!=">is not</MenuItem>
                                      </TextField>
                                    }
                                    {(values.actions[idx].attribute === 'sku' || values.actions[idx].attribute === '') &&
                                      <TextField
                                        fullWidth
                                        select
                                        label="Operator"
                                        name={`actions.${idx}.operator`}
                                        value={values.actions[idx].operator}
                                        onChange={(event) => {
                                          if (values.actions[idx].operator !== event.target.value) {
                                            setFieldValue(`actions.${idx}.value`, '');
                                          }

                                          handleChange(event);
                                        }}
                                        onBlur={handleBlur}
                                        error={touched.actions?.length &&
                                            errors.actions?.length &&
                                            touched?.actions[idx]?.operator &&
                                            Boolean(errors?.actions[idx]?.operator)
                                        }
                                        helperText={touched.actions?.length &&
                                            errors.actions?.length &&
                                            touched?.actions[idx]?.operator &&
                                            errors?.actions[idx]?.operator
                                        }
                                      >
                                        <MenuItem value="==">is</MenuItem>
                                        <MenuItem value="!=">is not</MenuItem>
                                        <MenuItem value="{}">contain</MenuItem>
                                        <MenuItem value="!{}">does not contain</MenuItem>
                                        <MenuItem value="()">is one of</MenuItem>
                                        <MenuItem value="!()">is not one of</MenuItem>
                                      </TextField>
                                    }
                                  </Grid>
                                  <Grid item xs={12} sm={4} md={3}>
                                    {values.actions[idx].attribute === 'category_ids' &&
                                      <TextField
                                        fullWidth
                                        select
                                        label="Value"
                                        name={`actions.${idx}.value`}
                                        value={values.actions[idx].value}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.actions?.length &&
                                            errors.actions?.length &&
                                            touched?.actions[idx]?.value &&
                                            Boolean(errors?.actions[idx]?.value)
                                        }
                                        helperText={touched.actions?.length &&
                                            errors.actions?.length &&
                                            touched?.actions[idx]?.value &&
                                            errors?.actions[idx]?.value
                                        }
                                      >
                                        {categories.map((category) => {
                                          return (
                                            <MenuItem value={category.id} key={category.id}>
                                              {category.nameWithPath}
                                            </MenuItem>
                                          );
                                        })}
                                      </TextField>
                                    }
                                    {values.actions[idx].attribute === 'sku' &&
                                    values.actions[idx].operator.includes('{}') &&
                                      <TextField
                                        fullWidth
                                        select
                                        label="Value"
                                        name={`actions.${idx}.value`}
                                        value={values.actions[idx].value}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.actions?.length &&
                                          errors.actions?.length &&
                                          touched?.actions[idx]?.value &&
                                          Boolean(errors?.actions[idx]?.value)
                                        }
                                        helperText={touched.actions?.length &&
                                          errors.actions?.length &&
                                          touched?.actions[idx]?.value &&
                                          errors?.actions[idx]?.value
                                        }
                                      >
                                        <MenuItem value="PROD">PROD</MenuItem>
                                        <MenuItem value="TREAT">TREAT</MenuItem>
                                        <MenuItem value="GIFT">GIFT</MenuItem>
                                        <MenuItem value="MEMBER">MEMBER</MenuItem>
                                      </TextField>
                                    }
                                    {(values.actions[idx].attribute === 'sku' || values.actions[idx].attribute === '') &&
                                      values.actions[idx].operator.includes('=') &&
                                      <TextField
                                        fullWidth
                                        select
                                        label="Value"
                                        name={`actions.${idx}.value`}
                                        value={values.actions[idx].value}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.actions?.length &&
                                            errors.actions?.length &&
                                            touched?.actions[idx]?.value &&
                                            Boolean(errors?.actions[idx]?.value)
                                        }
                                        helperText={touched.actions?.length &&
                                            errors.actions?.length &&
                                            touched?.actions[idx]?.value &&
                                            errors?.actions[idx]?.value
                                        }
                                      >
                                        {products.map((product) => {
                                          return (
                                            <MenuItem value={product.sku} key={product.sku}>
                                              {`${product.sku} (${product.name})`}
                                            </MenuItem>
                                          );
                                        })}
                                      </TextField>
                                    }
                                    {values.actions[idx].attribute === 'sku' &&
                                    values.actions[idx].operator.includes('()') &&
                                    <Autocomplete
                                      multiple
                                      fullWidth
                                      disableClearable
                                      size="small"
                                      name={`actions.${idx}.value`}
                                      options={products}
                                      getOptionLabel={(option) => `${option.name} (${option.sku})`}
                                      value={products.filter((product) => values.actions[idx].value.split(', ').includes(String(product.sku)))}
                                      onChange={addRemoveSelectOption}
                                      onBlur={addRemoveSelectOption}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Value"
                                          style={{paddingTop: '3px'}}
                                          error={touched.actions?.length &&
                                            errors.actions?.length &&
                                            touched.actions[idx]?.value &&
                                            Boolean(errors.actions[idx]?.value)
                                          }
                                          helperText={touched.actions?.length &&
                                              errors.actions?.length &&
                                              touched.actions[idx]?.value &&
                                              errors.actions[idx]?.value
                                          }
                                        />
                                      )}
                                    />
                                    }
                                    {(values.actions[idx].attribute === 'sku' || values.actions[idx].attribute === '') &&
                                      values.actions[idx].operator === '=' &&
                                      <TextField
                                        fullWidth
                                        select
                                        label="Value"
                                        name={`actions.${idx}.value`}
                                        value={values.actions[idx].value}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.actions?.length &&
                                            errors.actions?.length &&
                                            touched?.actions[idx]?.value &&
                                            Boolean(errors?.actions[idx]?.value)
                                        }
                                        helperText={touched.actions?.length &&
                                            errors.actions?.length &&
                                            touched?.actions[idx]?.value &&
                                            errors?.actions[idx]?.value
                                        }
                                      >
                                        {products.map((product) => {
                                          return (
                                            <MenuItem value={product.sku} key={product.sku}>
                                              {`${product.sku} (${product.name})`}
                                            </MenuItem>
                                          );
                                        })}
                                      </TextField>
                                    }
                                  </Grid>
                                  <Grid item xs={12} sm={4} md={3}>
                                    <Fab
                                      size="small"
                                      className="m-2"
                                      onClick={() => remove(idx)}
                                      title="Remove rule action"
                                    >
                                      <Icon>delete</Icon>
                                    </Fab>
                                  </Grid>
                                </Grid>
                              </Grid>
                            );
                          })}
                          <Fab
                            color="primary"
                            size="small"
                            className="m-2"
                            onClick={() => push(defaultActionData)}
                            title="Add new rule action"
                          >
                            <Icon>add</Icon>
                          </Fab>
                        </>
                      }
                    </>
                  )}
                </FieldArray>
                <Grid item xs={12} className="pt-8">
                  <Button
                    color="primary"
                    variant="contained"
                    className="mr-4"
                    type="submit"
                  >
                    Save
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setOpenEditRuleDialog(false)}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default EditReferralRule;
